import DotsHorizontal from "@untitled-ui/icons-react/build/esm/DotsHorizontal";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";
import { Tooltip } from "antd";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import styled from "styled-components";
import { ReactComponent as Turtle1 } from "../../../../assets/icons/Turtle_1_.svg";
import { ReactComponent as Eye } from "../../../../assets/icons/eye2.svg";
import { ReactComponent as Hourglass } from "../../../../assets/icons/hourglass-03.svg";
import { ReactComponent as Sunset0 } from "../../../../assets/icons/sunset0.svg";
import { ReactComponent as Sunrise0 } from "../../../../assets/icons/sunrise0.svg";
import { ReactComponent as ZapFast } from "../../../../assets/icons/zap-fast.svg";
import FormContainer from "../../../common/forms/FormContainer";
import SimpleListComponent from "../../../common/lists/SimpleListComponent";
import { TooltipExposureDays } from "./MetricsTooltips";

// #region SCOPED CSS
const StyledPage = styled.div`
    .gtin-kpis-list {
    }

    .table-structure {
        padding: 0 !important;
    }

    .Frame-111 {
        color: var(--text-icons-green-500, #0b0) !important;
        text-decoration-line: underline;
        cursor: pointer;
    }

    .info-circle {
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        position: relative;
        overflow: visible;
        top: 1px;
    }

    .statuses {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
        justify-content: flex-start;
        flex-shrink: 0;
        width: 108px;
        position: relative;
    }

    .sunset {
        flex-shrink: 0;
        width: 11px;
        height: 11px;
        position: relative;
        overflow: visible;
        aspect-ratio: 1;
        top: -1px;
    }

    ._1-9-dimming {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: left;
        font-family: var(--subtitle-2-font-family, "Roboto-Regular", sans-serif);
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
        position: relative;
    }

    //CUSTOM TABLE CSS
    .rdt_TableCol:first-child div[role="columnheader"] {
        display: flex;
        justify-content: left;
        width: 100% !important;
        padding-left: 8px;
    }

    .rdt_TableCol:not(:first-child) div[role="columnheader"],
    .rdt_TableCell[role="cell"] {
        text-align: left !important;
        width: 100% !important;
        display: grid !important;
    }

    .rdt_TableBody .rdt_TableCell[role="cell"] {
        padding-right: 8px;
    }

    .rdt_TableBody .rdt_TableCell[role="cell"]:first-child {
        display: flex !important;
        justify-content: left;
        padding-left: 8px;
    }

    .rdt_TableBody .rdt_TableCell[role="cell"]:first-child > div {
        padding-right: 5px;
    }

    .rdt_TableCol:last-child,
    .rdt_TableCell:last-child {
        display: flex !important;
        justify-content: right;
        padding-right: 15px !important;
    }

    .statuses {
        //justify-content: center;
        width: 100%;
    }
`;

const StyledTooltip = styled.div`
    text-align: left;
    font-size: 12px;
    font-weight: 400;
    position: relative;
    align-self: stretch;
`;
// #endregion

const GTINsKPIs = forwardRef((props, ref) => {
    // #region VARS
    const { onGtinClicked, kpiData, enabled, highlightedStock } = props;

    const columns = [
        {
            name: "GTIN",
            cell: row => (
                <div
                    className="Frame-111 text-left"
                    onClick={() => {
                        const gtin = row?.gtin === "All Variants" ? "All" : row?.gtin;
                        onGtinClicked(gtin);
                    }}>
                    {row?.gtin ?? "--"}
                </div>
            ),
            sortable: true,
            minWidth: "115px"
        },
        {
            name: "Color",
            cell: row => row?.color ?? "--",
            minWidth: "103px"
        },
        {
            name: "Size",
            cell: row => row?.size ?? "--",
            minWidth: "103px"
        },
        {
            name: (
                <span className="w-100">
                    Entry <br /> stock
                </span>
            ),
            cell: row => getIntInStringFormat(row?.totalStockArrival ?? "--"),
            minWidth: "70px"
        },
        {
            name: (
                <span className="w-100">
                    Items <br /> sold
                </span>
            ),
            cell: row => getIntInStringFormat(row?.itemsSold ?? "--"),
            minWidth: "72px"
        },
        {
            name: (
                <span className="w-100">
                    Overall <br /> stock
                </span>
            ),
            cell: row => getIntInStringFormat(row?.overallStock ?? "--"),
            minWidth: "54px"
        },
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Number of stores that currently have this variant <b>in stock.</b>
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    <span className="lh-normal">
                        In <br /> stores
                    </span>
                </div>
            ),
            cell: row => <span className="ml-16px">{getIntInStringFormat(row?.inStores ?? "--")}</span>,
            minWidth: "70px"
        },
        {
            name: (
                <div className="flex w-100">
                    <TooltipExposureDays enabled={enabled} />
                    <span className={highlightedStock ? "text-blue-600" : ""}>
                        Exposure <br /> (days)
                    </span>
                </div>
            ),
            cell: row => <span className="ml-16px">{getIntInStringFormat(row?.calendarDaysOfExposure ?? "--")}</span>,
            minWidth: "90px"
        },
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Shows how many calendar days are left until the selected item is no longer available in
                                the selected store(s). For multiple stores, this means the day the last selected store
                                runs out.
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    Out of stock
                    <br /> in (days)
                </div>
            ),
            cell: row => <span className="ml-16px">{getIntInStringFormat(row?.daysOfCoverFullStockOut ?? "--")}</span>,
            minWidth: "100px"
        },
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Variant STR in <b>units/day</b> for the selected store set.
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    STR
                </div>
            ),
            cell: row => <span className="ml-16px">{getDoubleInStringFormat(row?.sellThroughRate ?? "--", 3)}</span>,
            minWidth: "90px"
        },
        /* {
            name: (
                <span className="w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Shows how long the remaining stock will last across the selected stores combined, based
                                on their combined selling pace (stores sell in parallel). It does not mean every store
                                will be empty on that day.
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px" />
                    </Tooltip>
                    DOC <br /> blended
                </span>
            ),
            cell: row => getDoubleInStringFormat(row?.daysOfCoverBlended ?? "--", 1)
        },
        {
            name: (
                <span className="w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Shows when the last selected store will run out of stock, i.e., when the product is no
                                longer available in any of the selected stores.
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px" />
                    </Tooltip>
                    DOC <br /> stock out
                </span>
            ),
            cell: row => {
                if (row?.daysOfCoverFullStockOutEnum === "NO_VELOCITY") return "— / No velocity";
                return getDoubleInStringFormat(row?.daysOfCoverFullStockOut ?? "--", 1);
            }
        }, */
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Variant acceleration score for the last 2 weeks vs the previous 2 weeks (bounded -2..+2)
                                with Rising/Dimming/Idle label.
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    ACCEL <br /> 2 weeks
                </div>
            ),
            cell: row => {
                const accelerationEnum = row?.twoWeeksAccelerationEnum || "Idle";
                const accelerationColor =
                    accelerationEnum === "Rising" ? "#FFA500" : accelerationEnum === "Dimming" ? "#FF0000" : "#00AEEF";

                return (
                    <div className="statuses ml-16px">
                        {accelerationEnum === "Rising" ? (
                            <Sunrise0 alt="acceleration" className="sunset" style={{ color: accelerationColor }} />
                        ) : accelerationEnum === "Dimming" ? (
                            <Sunset0 alt="acceleration" className="sunset" style={{ color: accelerationColor }} />
                        ) : accelerationEnum === "Idle" ? (
                            <Hourglass alt="acceleration" className="sunset" style={{ color: accelerationColor }} />
                        ) : (
                            <></>
                        )}
                        <div className="_1-9-dimming">{accelerationEnum}</div>
                    </div>
                );
            },
            minWidth: "115px"
        },
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Speed label for this variant based on its total score (Fast / Watch List / Slow).
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    Shelf speed
                </div>
            ),
            cell: row => {
                //row.shelfSpeed = "Fast";

                const hasShelfSpeed = ["Fast", "Watchlist", "Watch List", "Slow"].includes(row?.shelfSpeed);
                if (!hasShelfSpeed) return <div className="ml-16px">--</div>;

                const shelfSpeedColor =
                    row?.shelfSpeed === "Fast"
                        ? "#00FF00"
                        : row?.shelfSpeed === "Watchlist" || row?.shelfSpeed === "Watch List"
                          ? "#FFA500"
                          : "#FF0000";

                const shelfSpeed = row?.shelfSpeed === "Watchlist" ? "Watch List" : row?.shelfSpeed;
                const shelfSpeedText =
                    shelfSpeed === "Watchlist" || shelfSpeed === "Watch List" ? shelfSpeed : `${shelfSpeed} mover`;

                return (
                    <div className="statuses ml-16px">
                        {shelfSpeed === "Fast" ? (
                            <ZapFast alt="acceleration" className="sunset" style={{ color: shelfSpeedColor }} />
                        ) : shelfSpeed === "Slow" ? (
                            <Turtle1 alt="acceleration" className="sunset" style={{ color: shelfSpeedColor }} />
                        ) : (
                            <Eye alt="acceleration" className="sunset" style={{ color: shelfSpeedColor }} />
                        )}
                        <div className="_1-9-dimming">{shelfSpeedText}</div>
                    </div>
                );
            },
            minWidth: "105px"
        },
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Stock-pressure factor used by the scoring/reorder logic to avoid over-ordering when
                                retail stock levels are already high (configurable business rule).
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    Cut off <br /> pressure stock
                </div>
            ),
            cell: row => <div className="ml-16px">{getIntInStringFormat(row?.cutOffPressureStock ?? "--")}</div>,
            minWidth: "103px"
        },
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                How urgent it is to act on this variant (typically driven by low DOC and/or strong
                                positive/negative trend). Higher = act sooner.
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    Urgency <br /> score
                </div>
            ),
            cell: row => <div className="ml-16px">{getDoubleInStringFormat(row?.urgencyScore ?? "--", 2)}</div>,
            minWidth: "108px"
        },
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Ranking weight used to prioritize actions across variants (e.g., which
                                reorders/activations should be handled first).
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    Priority <br /> score
                </div>
            ),
            cell: row => <div className="ml-16px">{getDoubleInStringFormat(row?.priorityScore ?? "--", 2)}</div>,
            minWidth: "100px"
        },
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Seasonality adjustment applied to the score/reorder logic (e.g., reduce priority if near
                                end-of-season).
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    Season <br /> factor
                </div>
            ),
            cell: row => <div className="ml-16px">{getDoubleInStringFormat(row?.seasonFactor ?? "--", 2)}</div>,
            minWidth: "90px"
        },
        {
            name: (
                <div className="flex w-100">
                    <Tooltip
                        color="#10141b"
                        overlayInnerStyle={{ textAlign: "center" }}
                        title={
                            <StyledTooltip>
                                Recommended reorder quantity for this variant for the selected scope, based on target
                                coverage vs current stock and current STR (and may be adjusted by ACCEL/season rules).
                            </StyledTooltip>
                        }>
                        <InfoCircle className="info-circle mr-4px align-self-center" />
                    </Tooltip>
                    Reorder <br /> quantity
                </div>
            ),
            cell: row => <div className="ml-16px">{getIntInStringFormat(row?.reorderQuantity ?? "--")}</div>,
            minWidth: "80px"
        },
        {
            name: "",
            hideHeader: true,
            cell: () => (
                <span className="ant-dropdown-link" style={{ color: "white", cursor: "pointer" }}>
                    <DotsHorizontal height="16" color="#D1D1D1" />
                </span>
            ),
            minWidth: "70px"
        }
    ];
    // #endregion

    // #region EFFECTS & MEMOS
    const getListData = useMemo(() => {
        if (!kpiData) return [];

        /*
        gtinsData row example: 
        {
            "_id": "693616f10c0db5f44221f0c3",
            "barcode": "5714196141007",
            "colorName": "Black",
            "colorHexacode": "000000",
            "size": "3pcs",
            "images": [
                {
                    "name": "-",
                    "URL": "https://hvisk.spysystem.dk/file/377ac0a6-72c4-49e0-ba93-1dec69ad2133/8c2032d6-5e8b-4915-a28b-2339fb7af415/IMG_9106-3.png"
                }
            ],
            "stockAndPricesData": [
                {
                    "stockLog": [
                        {
                            "stock": 7,
                            "date": "2025-12-09T17:38:11.778Z"
                        }
                    ]
                }
            ],
            "totalStock": 7,
            "totalStoresWithStock": 1,
            "totalStockArrival": 7,
            "fromDate": "2025-12-09T17:38:11.778Z",
            "toDate": "2025-12-09T17:38:11.778Z",
            "unitsSold": 0,
            "sellThroughRate": 0,
            "daysOfCover": null,
            "daysOfCoverEnum": "NO_VELOCITY",
            "daysOfCoverBlended": null,
            "daysOfCoverFullStockOut": null,
            "acceleration": null,
            "accelerationEnum": null,
            "totalScore": null,
            "unitsSoldPercentage": 0,
            "shelfSpeed": "Slow",
            "cutOffPressureStock": null,
            "urgencyScore": null,
            "priorityScore": null,
            "seasonFactor": null,
            "reorderQuantity": null,
            "totalSales": null,
            "grossMargin": null,
            "returns": null,
            "graphData": [
                {
                    "date": "2025-12-09",
                    "stockQuantity": 7,
                    "sellThroughRate": 0,
                    "daysOfCover": null,
                    "acceleration": null
                }
            ]
        }

        */

        const result = kpiData?.gtinsData?.map(gtinData => {
            return {
                gtin: gtinData?.barcode,
                color: gtinData?.colorName,
                size: gtinData?.size,
                overallStock: gtinData?.totalStock,
                inStores: gtinData?.totalStoresWithStock,
                itemsSold: gtinData?.unitsSold,
                daysOfExposure: gtinData?.daysOfExposure, //NOT USED RIGHT NOW
                calendarDaysOfExposure: gtinData?.calendarDaysOfExposure,
                sellThroughRate: gtinData?.sellThroughRate,
                daysOfCoverBlended: gtinData?.daysOfCoverBlended, //NOT USED RIGHT NOW
                daysOfCoverBlendedEnum: gtinData?.daysOfCoverBlendedEnum, //NOT USED RIGHT NOW
                daysOfCoverFullStockOut: gtinData?.daysOfCoverFullStockOut, //NOT USED RIGHT NOW
                daysOfCoverFullStockOutEnum: gtinData?.daysOfCoverFullStockOutEnum, //NOT USED RIGHT NOW
                twoWeeksAcceleration: gtinData?.acceleration,
                twoWeeksAccelerationEnum: gtinData?.accelerationEnum,
                totalScore: gtinData?.totalScore,
                shelfSpeed: gtinData?.shelfSpeed,
                cutOffPressureStock: gtinData?.cutOffPressureStock,
                urgencyScore: gtinData?.urgencyScore,
                priorityScore: gtinData?.priorityScore,
                seasonFactor: gtinData?.seasonFactor,
                reorderQuantity: gtinData?.reorderQuantity,
                totalStockArrival: gtinData?.totalStockArrival
            };
        });

        const allVariants = kpiData?.allVariantsData;
        result.unshift({
            gtin: "All Variants",
            color: "All",
            size: "All",
            overallStock: allVariants?.totalStock,
            inStores: allVariants?.totalStoresWithStock,
            itemsSold: allVariants?.unitsSold,
            daysOfExposure: allVariants?.daysOfExposure,
            calendarDaysOfExposure: allVariants?.calendarDaysOfExposure,
            sellThroughRate: allVariants?.sellThroughRate,
            daysOfCoverBlended: allVariants?.daysOfCoverBlended,
            daysOfCoverBlendedEnum: allVariants?.daysOfCoverBlendedEnum,
            daysOfCoverFullStockOut: allVariants?.daysOfCoverFullStockOut,
            daysOfCoverFullStockOutEnum: allVariants?.daysOfCoverFullStockOutEnum,
            twoWeeksAcceleration: allVariants?.acceleration,
            twoWeeksAccelerationEnum: allVariants?.accelerationEnum,
            totalScore: allVariants?.totalScore,
            shelfSpeed: allVariants?.shelfSpeed,
            cutOffPressureStock: allVariants?.cutOffPressureStock,
            urgencyScore: allVariants?.urgencyScore,
            priorityScore: allVariants?.priorityScore,
            seasonFactor: allVariants?.seasonFactor,
            reorderQuantity: allVariants?.reorderQuantity,
            totalStockArrival: allVariants?.totalStockArrival
        });

        return result;
    }, [kpiData]);

    /*const onExportClick = id => {
        if (!getListData?.length) {
            Library.showToastMessage({
                type: "error",
                message: "No KPI data available!"
            });
            return;
        }

        const getExportData = () => {
            return getListData.map(row => ({
                "GTIN": row.gtin,
                "Color": row.color,
                "Size": row.size,
                "Overall Stock": row.overallStock,
                "In Stores": row.inStores,
                "Items Sold": row.itemsSold,
                "Days of Exposure": row.daysOfExposure,
                "STR": row.sellThroughRate,
                "DOC Blended": row.daysOfCoverBlended,
                "DOC Stock Out":
                    row.daysOfCoverFullStockOutEnum === "NO_VELOCITY" ? "No Velocity" : row.daysOfCoverFullStockOut,
                "ACCEL 2 weeks": row.twoWeeksAccelerationEnum,
                "Shelf Speed": row.shelfSpeed,
                "Cut off pressure stock": row.cutOffPressureStock,
                "Urgency score": row.urgencyScore,
                "Priority score": row.priorityScore,
                "Season factor": row.seasonFactor,
                "Reorder quantity": row.reorderQuantity
            }));
        };

        if (id === "copy") {
            //use getListData and save it to clipboard
            const kpiDataString = JSON.stringify(getListData);
            navigator.clipboard.writeText(kpiDataString);

            Library.showToastMessage({
                type: "success",
                message: "KPI data copied to clipboard"
            });
        } else if (id === "csv") {
            try {
                const data = getExportData();
                const ws = XLSX.utils.json_to_sheet(data);
                const csv = XLSX.utils.sheet_to_csv(ws);
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                const link = document.createElement("a");
                const url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", "GTINs_KPIs.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error("Export Error", error);
                Library.showToastMessage({ type: "error", message: "Failed to export CSV" });
            }
        } else if (id === "excel") {
            try {
                const data = getExportData();
                const ws = XLSX.utils.json_to_sheet(data);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "KPIs");
                XLSX.writeFile(wb, "GTINs_KPIs.xlsx");
            } catch (error) {
                console.error("Export Error", error);
                Library.showToastMessage({ type: "error", message: "Failed to export Excel" });
            }
        } else if (id === "print" || id === "pdf") {
            try {
                const data = getExportData();
                const html = `
                <html>
                    <head>
                        <title>GTINs KPIs</title>
                        <style>
                            table { width: 100%; border-collapse: collapse; font-family: sans-serif; font-size: 10px; }
                            th, td { border: 1px solid #ddd; padding: 4px; text-align: right; }
                            th { background-color: #f2f2f2; text-align: center; }
                            td:first-child, td:nth-child(2), td:nth-child(3) { text-align: left; }
                            h1 { font-family: sans-serif; font-size: 18px; margin-bottom: 10px; }
                        </style>
                    </head>
                    <body>
                        <h1>GTINs KPIs</h1>
                        <table>
                            <thead>
                                <tr>${Object.keys(data[0] || {})
                                    .map(key => `<th>${key}</th>`)
                                    .join("")}</tr>
                            </thead>
                            <tbody>
                                ${data
                                    .map(
                                        row =>
                                            `<tr>${Object.values(row)
                                                .map(val => `<td>${val ?? "--"}</td>`)
                                                .join("")}</tr>`
                                    )
                                    .join("")}
                            </tbody>
                        </table>
                    </body>
                </html>
            `;

                const printWindow = window.open("", "_blank", "width=800,height=600");
                if (printWindow) {
                    printWindow.document.write(html);
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => {
                        printWindow.print();
                        if (id === "pdf") {
                            Library.showToastMessage({
                                type: "info",
                                message: "Please select 'Save as PDF' in the print dialog."
                            });
                        }
                    }, 500);
                }
            } catch (error) {
                console.error("Export Error", error);
                Library.showToastMessage({ type: "error", message: "Failed to initialize print" });
            }
        }
    };

    // onExportClick is used in useImperativeHandle
    const onExportClickHandle = onExportClick;
    */
    const handleResetView = () => {};

    // #endregion

    // #region RENDER
    useImperativeHandle(ref, () => ({
        //onExportClick: onExportClickHandle,
        resetView: handleResetView
    }));

    return (
        <StyledPage className="w-100">
            <FormContainer
                className="w-100"
                title={
                    <div className={`flex gap-12px ${enabled ? "text-white" : "text-inactive"} text-transform-none`}>
                        GTINs KPIs
                    </div>
                }>
                <SimpleListComponent
                    className="gtin-kpis-table w-100"
                    tableClassName="gtin-kpis-list w-100"
                    columns={columns}
                    listData={getListData}
                    selectableRows={false}
                    pagination={false}
                    showHeader={false}
                    enabled={enabled}
                />
            </FormContainer>
        </StyledPage>
    );
    // #endregion
});

export default GTINsKPIs;

// #region EXPORTED METHODS
export const getDoubleInStringFormat = (value, decimals = 2) => {
    let doubleValue;
    //if value is string, try to parse it to double, if error then return as it is
    if (typeof value === "string") {
        try {
            doubleValue = parseFloat(value);
            if (isNaN(doubleValue)) return value;
        } catch (error) {
            return value;
        }
    } else doubleValue = value;

    if (doubleValue === null || doubleValue === undefined) return "--";

    return doubleValue.toLocaleString("de-DE", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

export const getIntInStringFormat = value => {
    return getDoubleInStringFormat(value, 0);
};
// #endregion
